(ns machtfit.startpage.catalog
  (:require [cljs-time.core :as time]
            [clojure.string :as str]
            [goog.string :as gstring]
            [machtfit.app.routes :as routes]
            [machtfit.app.tracking :as tracking]
            [machtfit.core :as machtfit]
            [machtfit.datetime :as datetime]
            [machtfit.format :as format]
            [machtfit.human :as human]
            [machtfit.responsive :as responsive]
            [machtfit.startpage.events :as events]
            [machtfit.startpage.filterbar :as filterbar]
            [machtfit.startpage.icons :as icons]
            [machtfit.startpage.maps :as maps]
            [machtfit.startpage.statics :as statics]
            [machtfit.startpage.subs :as subs]
            [machtfit.translation :refer [tr t trp-with-place-holders]]
            [machtfit.widgets.badge :as badge]
            [machtfit.widgets.button :as button]
            [machtfit.widgets.countdown :as countdown]
            [re-frame.core :as rf]
            [reagent.core :as r]))

(defn- formatted-price-element [price]
  (let [formatted-price (gstring/format "%.2f" price)
        [amount decimal-amount] (str/split formatted-price #"\.")]
    [:span.text-black.break-keep.flex.items-center
     [:span.leading-8.text-2xl.sm:text-3xl (format/decimal amount)]
     [:sup.leading-4.text-xs.sm:text-sm.pt-1 (str decimal-amount " €")]]))

(defn wellhub-pricing [subsidy]
  (let [formatted-price (gstring/format "%.2f" 0)
        [amount decimal-amount] (str/split formatted-price #"\.")]
    [:div.flex.flex-col.items-start.gap-2
     [:span.text-black.break-keep.flex.items-center
      [:span.leading-8.text-2xl.sm:text-3xl (t {:de "ab "
                                                :from "from "})
       amount]
      [:sup.leading-4.text-xs.sm:text-sm.pt-1 (str decimal-amount " € / Monat")]]
     (when subsidy
       [:span.flex.items-center.gap-2.flex-wrap
        [badge/pill-outline {:text (str (t {:de "Spare "
                                            :en "Save "})
                                        (format/currency subsidy))
                             :color :brand
                             :classes ["sm:h-6" "sm:px-2.5" "sm:text-sm"]
                             :size :sm}]
        [:span.text-xs.block.sm:hidden (t {:de "mit Guthaben"
                                           :en "with budget"})]
        [:span.text-xs.hidden.sm:block (t {:de "mit deinem Guthaben"
                                           :en "with your budget"})]])]))

(defn card-product [{:keys [id
                            title
                            category
                            image-url
                            image-2x-url
                            estg3no34-subsidizable?
                            is-app?
                            is-stream?
                            price
                            labels
                            promoted?
                            promotion?
                            promotion-countdown?
                            promotion-border-color
                            promotion-end-time
                            provider-id
                            wellhub-promotion?
                            supplier]
                     :as product}]
  (let [has-discount? (not (zero? (:machtfit-discount price)))
        link-attributes {:href (str "/angebote/" id "/")}
        appointments @(rf/subscribe [::subs/current-dates-for-product id])
        next-appointment @(rf/subscribe [::subs/next-datetime-for-product id])
        membership-product? (some? provider-id)
        provider-info (when provider-id
                        (first (filter (comp #{provider-id} :id)
                                       @(rf/subscribe [:get [:user :current-providers-for-user]]))))
        wellhub-product? (= (:name provider-info) "Wellhub")
        wellhub? (or wellhub-promotion? wellhub-product?)
        mindance? (or
                   (= (:name provider-info) "Mindance")
                   (some #{id} @(rf/subscribe [:get [:ui :mindance :voucher-offers]])))
        subdomain-color @(rf/subscribe [:get [:subdomain-info :base-color]])
        countdown-active? (and promotion-countdown?
                               promotion-end-time
                               (datetime/countdown-active? (datetime/unix->date promotion-end-time)
                                                           (time/hours 48)))
        {:keys [machtfit-discount
                machtfit-discount-type
                discounted-price
                subsidy-by-employer
                list-price
                hide-price-info?]} price]
    [:a.flex.gap-2.sm:gap-0.cursor-pointer.overflow-hidden.card-product
     (merge link-attributes
            {:data-test-product-requires-payment (> discounted-price 0)
             :data-test-product-has-multiple-appointments (boolean (seq appointments))}
            (when (or promoted? promotion?)
              {:style {:box-shadow (str "0 0 0 4px " (if (not (str/blank? promotion-border-color))
                                                       promotion-border-color
                                                       subdomain-color)
                                        "80")}}))
     [:div.flex.flex-col
      {:class "image-container gap-2.5"}
      [:picture.overflow-hidden.image
       [:source {:src-set (str image-url " 1x, " image-2x-url " 2x")}]
       [:img.h-full.w-full.object-cover {:src image-url
                                         :alt (str "Bild zum Angebot „" (:title product) "”")}]]
      [:div.flex.flex-wrap.gap-1.card-badges
       (when wellhub?
         [badge/pill-outline {:text (t {:de "Wellhub"
                                        :en "Wellhub"})
                              :icon-leading "g8"
                              :color :wellhub
                              :size :sm}])
       (when mindance?
         [badge/pill-outline {:text (t {:de "Mindance"
                                        :en "Mindance"})
                              :icon-leading "mindance"
                              :color :mindance
                              :size :sm}])
       (when estg3no34-subsidizable?
         [badge/pill-outline {:text (t {:de "Prävention"
                                        :en "Prevention"})
                              :icon-leading "check-heart"
                              :size :sm}])
       (when (= (:type product) "online")
         [badge/pill-outline {:text (t {:de "Online"
                                        :en "Online"})
                              :icon-leading "monitor-04"
                              :size :sm}])
       (when (= (:type product) "with-location")
         [badge/pill-outline {:text (if (:distance product)
                                      (gstring/format "%.1f km" (/ (:distance product) 1000))
                                      (t {:de "vor Ort"
                                          :en "on site"}))
                              :icon-leading "marker-pin-01"
                              :size :sm}])
       (when (or is-app? mindance?)
         [badge/pill-outline {:text (t {:de "App"
                                        :en "App"})
                              :icon-leading "phone-01"
                              :size :sm}])
       (when (= category "Gesundheitsreisen")
         [badge/pill-outline {:text (t {:de "Reise"
                                        :en "Travel"})
                              :icon-leading "plane"
                              :size :sm}])
       (when is-stream?
         [badge/pill-outline {:text (t {:de "Stream"
                                        :en "Stream"})
                              :icon-leading "play"
                              :size :sm}])
       (when (= (:type product) "product")
         [badge/pill-outline {:text (t {:de "Produkt"
                                        :en "Product"})
                              :icon-leading "shopping-bag-01"
                              :size :sm}])]]
     [:div.flex.flex-col.gap-2.sm:gap-4.w-full.content
      (when (or promotion?
                has-discount?
                (labels "Beliebt"))
        [:div.flex.gap-1.campaign-badges
         (if countdown-active?
           [countdown/main promotion-end-time
            {:display-type :badge
             :countdown-label {:de "Endet in: "
                               :en "Ends in: "}}]
           [:<>
            (when (labels "Beliebt")
              [badge/pill-filled {:text (t {:de "Beliebt"
                                            :en "Popular"})
                                  :color :unset
                                  :classes ["bg-raspberrypink-500" "text-white" "sm:h-6" "sm:px-2.5" "sm:text-sm"]
                                  :size :sm}])
            (when promotion?
              [badge/pill-outline {:text (t {:de "Aktion"
                                             :en "Promotion"})
                                   :color :brand
                                   :classes ["sm:h-6" "sm:px-2.5" "sm:text-sm"]
                                   :size :sm}])
            (when has-discount?
              [badge/pill-outline {:text (t {:de "Rabatt"
                                             :en "Discount"})
                                   :color :brand
                                   :classes ["sm:h-6" "sm:px-2.5" "sm:text-sm"]
                                   :size :sm}])])])
      [:div.flex.flex-col.gap-1
       [:span.text-gray-600.text-xs.sm:text-sm category]
       (when supplier
         [:span.text-xs.md:text-base.font-bold supplier])
       [:span.text-sm.sm:text-base title]]

      (let [number-of-appointments (and next-appointment (count appointments))]
        (if (seq (:weekdays product))
          [:div.text-gray-500.text-sm.font-normal.leading-5.overflow-hidden.line-clamp-2
           (clojure.string/join
            ", "
            (mapcat (fn [[weekday-key times]]
                      (map #(str (t (:short (human/weekday weekday-key))) ". "
                                 (human/timeformat %)
                                 (t {:de " Uhr" :en ""}))
                           times))
                    (:weekdays product)))]

          (when number-of-appointments
            [:div.flex.flex-col.text-gray-500.text-sm.font-normal.leading-5
             [:span
              (str (t (datetime/format-short-weekday next-appointment)) ". "
                   (datetime/format-date next-appointment) " "
                   (tr " um ")
                   (datetime/format-time next-appointment) (t {:de " Uhr" :en ""}))]

             (when (> number-of-appointments 1)
               [:span.text-gray-500.underline.cursor-pointer
                (if (= number-of-appointments 2)
                  (t {:de "+ 1 weiterer Termin" :en "+ 1 more appointment"})
                  (str "+ " (dec number-of-appointments) " "
                       (t {:de "weitere Termine" :en "more appointments"})))])])))

      (if wellhub?
        [wellhub-pricing (:subsidy provider-info)]
        (when-not hide-price-info?
          (if (zero? list-price)
            [badge/pill-outline {:text (t {:de "kostenlos"
                                           :en "free"})
                                 :color :brand
                                 :classes ["sm:h-6" "sm:px-2.5" "sm:text-sm"]
                                 :size :sm}]
            [:div.flex.flex-col.items-start.gap-2
             [:span.flex.items-end.gap-2.flex-wrap
              (when (and has-discount?
                         (pos? discounted-price)
                         (not membership-product?))
                [:span.text-brand-500.flex.items-center.break-keep
                 {:class "gap-[2px]"}
                 [:span.leading-8.text-2xl.sm:text-3xl machtfit-discount]
                 [:sup.leading-4.text-xs.sm:text-sm.pt-1 machtfit-discount-type]])
              [formatted-price-element discounted-price]
              (when-not (= discounted-price list-price)
                [:span.text-gray-600.text-xs.sm:text-sm.py-1.flex.justify-center.gap-1
                 (t {:de "Statt:"
                     :en "Before:"})
                 [:span.line-through (format/currency list-price)]])]
             (when (> subsidy-by-employer 0)
               [:span.flex.items-center.gap-2.flex-wrap
                [badge/pill-outline {:text (str (t {:de "Spare "
                                                    :en "Save "})
                                                (format/currency subsidy-by-employer))
                                     :color :brand
                                     :classes ["sm:h-6" "sm:px-2.5" "sm:text-sm"]
                                     :size :sm}]
                [:span.text-xs.block.sm:hidden (t {:de "mit Guthaben"
                                                   :en "with budget"})]
                [:span.text-xs.hidden.sm:block (t {:de "mit deinem Guthaben"
                                                   :en "with your budget"})]])])))]]))

(defn- slider []
  (let [slider-items @(rf/subscribe [:get [:slider :items]])
        items-count (count slider-items)
                                        ; basic slider settings
                                        ; ----
        shown-slides 4
        auto-slide? true
        auto-slide-time 8000
        slider-nav? true
        slider-dots? false
                                        ; ----
        step-animation (/ 100 shown-slides)
        max-steps (if (> 0 (- items-count shown-slides))
                    0
                    (- items-count shown-slides))
        current-step (r/atom 0)
        mouse-on-slider? (r/atom false)
        base-color @(rf/subscribe [:get [:subdomain-info :base-color]])]
    (js/setInterval #(when (and (not @mouse-on-slider?) auto-slide?)
                       (if (< @current-step max-steps)
                         (swap! current-step inc)
                         (reset! current-step 0)))
                    auto-slide-time)
    (fn []
      [:section
       [:div.container
        [:div.slider {:on-mouse-enter #(reset! mouse-on-slider? true)
                      :on-mouse-leave #(reset! mouse-on-slider? false)}
         (when slider-nav?
           [:button.slider-nav.slider-nav--prev
            {:on-click #(swap! current-step dec)
             :disabled (= 0 @current-step)}
            [icons/arrow-left]])
         (when slider-nav?
           [:button.slider-nav.slider-nav--next
            {:on-click #(swap! current-step inc)
             :disabled (= max-steps @current-step)}
            [icons/arrow-right]])
         [:div.items-grid.slider__track-wrapper
          [:div.row.slider__track {:style {:transform (str "translateX(-" (* @current-step step-animation) "%)")}}
           (for [slider-item slider-items]
             ^{:key (:name slider-item)}
             [:div.grid-item.slider__item {:style {:flex (str "0 0 " step-animation "%")
                                                   :max-width (str step-animation "%")}}
              [:a.card-service {:href (:url slider-item)
                                :target "_blank"}
               [:figure [:img {:src (:image-url slider-item)}]]
               [:p [:strong (:name slider-item)] ": " (:text slider-item)]]])]]
         (when slider-dots?
           [:div.slider-dots
            (for [n (range (inc max-steps))]
              [:button {:on-click #(reset! current-step n)
                        :style (when (= @current-step n) {:background base-color})}])])]]])))

(defn- filters []
  [:div.container.filter-container
   [:div
    {:class "filter"}
    [filterbar/filterbar]]])

(defn- no-results []
  (let [first-non-empty-alternate-type @(rf/subscribe [::subs/first-non-empty-alternate-product-type-filter])
        alt-filter-name (case first-non-empty-alternate-type
                          "product" {:de "Produkte"
                                     :en "product"}
                          "online" {:de "Online"
                                    :en "online"}
                          "with-location" {:de "vor Ort"
                                           :en "local"}
                          nil)]
    [:div.container
     [:div.no-results-container
      [:div.no-results
       [:span (t {:de "Leider können wir dir kein passendes Angebot anzeigen."
                  :en "Unfortunately we can't show you any matching offer."})]
       (if alt-filter-name
         [:<>
          [:button
           {:on-click #(rf/dispatch [::filterbar/toggle-product-types first-non-empty-alternate-type])}
           (trp-with-place-holders
            {:de "Passende {alt-filter-name}-Angebote anzeigen"
             :en "View suitable {alt-filter-name} offers"}
            {"alt-filter-name" (t alt-filter-name)})]
          [:a {:on-click #(js/history.go -1)}
           (t {:de "Filter einen Schritt zurücksetzen"
               :en "Reset filter one step"})]]
         [:button {:on-click #(js/history.go -1)}
          (t {:de "Filter einen Schritt zurücksetzen"
              :en "Reset filter one step"})])]]

     [:div.items-grid
      [:h4 (tr "Vielleicht findest du ja eine Anregung in unserem Magazin.")]
      [:div.row
       [:div.col-4.grid-item {:style {:padding-bottom "22px"}}
        [:a.card-service {:href "/content/magazin/tag/trainingsplane/"}
         [:figure [:img {:src (machtfit/static "img/trainingsplans.jpg")}]]
         [:h3 (tr "Trainingspläne")]]]
       [:div.col-4.grid-item {:style {:padding-bottom "22px"}}
        [:a.card-service {:href "/content/magazin/tag/fitness-ernahrungsbasics/"}
         [:figure [:img {:src (machtfit/static "img/fitness.jpg")}]]
         [:h3 (tr "Fitness- und Ernährungsbasics")]]]
       [:div.col-4.grid-item {:style {:padding-bottom "22px"}}
        [:a.card-service {:href "/content/magazin/tag/rezepte/"}
         [:figure [:img {:src (machtfit/static "img/recipes.jpg")}]]
         [:h3 (tr "Rezepte")]]]]]]))

(def ^:private summer-week-related-filters
  (array-map
   "Sommerwochen2024_Bewegung" (-> statics/filter-presets :exercise :filter)
   "Sommerwochen2024_Ernährung" (-> statics/filter-presets :nutrition :filter)
   "Sommerwochen2024_Entspannung" (-> statics/filter-presets :relaxation :filter)
   "Sommerwochen2024" {:area :machtfit}))

(rf/reg-sub ::summer-week-related-filter
  :<- [::subs/effective-filter]
  (fn [effective-filter _]
    (let [summer-week-label (some (:labels effective-filter)
                                  (keys summer-week-related-filters))]
      (get summer-week-related-filters summer-week-label))))

(defn- summer-week-cta []
  (when-let [filter @(rf/subscribe [::summer-week-related-filter])]
    [:div.mt-8.text-center
     [:div.mb-6
      [t {:de "Noch nichts passendes dabei?"
          :en " Nothing suitable yet?"}]]
     [button/button
      {:text [t {:de "Jetzt noch mehr Angebote entdecken"
                 :en "Discover even more offers now"}]
       :attrs (assoc (routes/attrs-link-to-page {:id :catalog
                                                 :filter filter}
                                                {:scroll-to-top? true})
                     :data-ph-capture-attribute-ui_id
                     (tracking/compose-ui-id "catalog" "dev_1245_cta_button"))}]]))

(defn- featured-article [id]
  (let [article @(rf/subscribe [::subs/article-by-id id])]
    [:a.featured-article {:href (:url article)}
     [:figure.bg-wrapper {:style {:background-image (str "url(" (:image-url article) ")")}}]
     [:div.featured-article__intro
      [:p (datetime/format-date (:created-at article))]
      [:h3 (:title article)]
      [:span.btn-2019 {:style @(rf/subscribe [::subs/button-color])} (tr "Jetzt Artikel lesen")]]]))

(defn- articles-list [articles]
  [:ul.articles-list
   (doall
    (for [article-id articles]
      (let [article @(rf/subscribe [::subs/article-by-id article-id])]
        ^{:key article-id}
        [:li
         [:a {:href (:url article)}
          [:figure.img-wrapper [:div.img-wrapper--inner {:style {:background-image (str "url(" (:image-url article) ")")}}]]
          [:div.article-intro
           [:h4 (:title article)]
           [:p {:dangerouslySetInnerHTML (r/unsafe-html (:teaser article))}]
           [:span.more (tr "Artikel lesen")]]]])))])

(defn- card [position {:keys [id type ids]}]
  (let [grid-product-class "grid-product"]
    ^{:key (or id (str "index-" position))}
    [:div
     {:class (case type
               :placeholder (str "col-4 col-4-lg grid-item content-loader " grid-product-class)
               :product (str "col-4 col-4-lg grid-item " grid-product-class)
               :featured-article "grid-item grid-featured-article"
               :articles-list "grid-item grid-articles-list")

      :data-ph-capture-attribute-card_position_in_list position
      :data-ph-capture-attribute-health_goal_match (when (and (= type :product)
                                                              @(rf/subscribe [::subs/health-goal-sort?]))
                                                     @(rf/subscribe [::subs/health-goal-match? id]))}

     (case type
       :placeholder [icons/content-loader]
       :product [card-product @(rf/subscribe [::subs/product-for-card id])]
       :featured-article [featured-article id]
       :articles-list [articles-list ids])]))

(defn- grid-products-articles []
  (let [show-map? @(rf/subscribe [::subs/show-map?])
        has-products? @(rf/subscribe [::subs/has-products-after-filter?])
        products-loaded? @(rf/subscribe [::subs/products-loaded?])
        show-products @(rf/subscribe [:get [:ui :show-products]])
        kilometers @(rf/subscribe [::subs/next-offer-distance])]
    [:section.items-grid {:style {:padding "0"}}
     [:div.container

      [:div.product-list-header
       (if @(rf/subscribe [::subs/products-loaded?])
         [:div {:style {:display "flex"
                        :align-items "center"
                        :justify-content "flex-end"
                        :width "100%"}}]
         (when-not show-map?
           [:h3 (t {:de "Angebote in Deiner Nähe werden gesucht..."
                    :en "Looking for offers close to you..."})]))]
      (if (and products-loaded?
               (not show-map?)
               (not has-products?))
        [no-results]

        [:div.row
         (if show-map?
           [:<>
            (when-let [products-location @(rf/subscribe [:get [:products-location :coordinates]])]
              [maps/ol-map
               products-location
               @(rf/subscribe [::subs/product-coordinates-for-map])])]
           (let [products (take show-products @(rf/subscribe [::subs/products-sorted]))
                 bootstrap-grid-tier @(rf/subscribe [::responsive/bootstrap-grid-tier])
                 {:keys [product]} (responsive/cards-by-grid-tier bootstrap-grid-tier)
                 items (case @(rf/subscribe [::subs/effective-filter :area])
                         :company products
                         (concat (take product products)
                                 (take (* product 3) (drop product products))
                                 (drop (* 4 product) products)))]
             (into [:<>] (map-indexed card) items)))])
      (when-not (> @(rf/subscribe [::subs/count-products-after-filter]) show-products)
        [summer-week-cta])
      [:div {:style {:margin "40px auto"
                     :text-align "center"}}
       (cond
         (and (> @(rf/subscribe [::subs/count-products-after-filter]) show-products)
              (not show-map?))
         [button/button
          {:text (t {:de "mehr Angebote anzeigen"
                     :en "show more offers"})
           :attrs {:style {:overflow-anchor "none"}
                   :data-ph-capture-attribute-ui_id "catalog__show_more_offers"
                   :on-click #(rf/dispatch [::events/load-more-clicked])}}]

         (and (some? kilometers)
              (= @(rf/subscribe [::subs/effective-filter :area]) :machtfit)
              (= @(rf/subscribe [::subs/effective-filter :sort-by]) :distance))
         [button/button
          {:text (gstring/format (t {:de "Für mehr Angebote den Umkreis auf %d km anpassen"
                                     :en "For more offers adjust the radius to %d km"})
                                 kilometers)
           :attrs {:on-click (comp #(rf/dispatch [::events/filter-distance-changed
                                                  kilometers
                                                  {:type "distance_extension"}])
                                   machtfit/stop-event)}}]

         (and (> @(rf/subscribe [::subs/count-products-not-fixed]) 0)
              (not @(rf/subscribe [::subs/effective-filter :online]))
              (= @(rf/subscribe [::subs/effective-filter :sort-by]) :distance))
         [button/button
          {:text (t {:de "Jetzt Online-Angebote ansehen"
                     :en "View online offers now"})
           :attrs {:on-click (comp (fn []
                                     (js/window.scrollTo 0 0)
                                     (rf/dispatch [::events/show-online-clicked]))
                                   machtfit/stop-event)}}])]]]))

(defn map-toggle-mobile []
  (when-not @(rf/subscribe [:get [:user :machtfit-area-only-online?]])
    [:div.map-toggle-mobile-container
     [filterbar/map-toggle]]))

(defn product-list []
  (let [slider-items? @(rf/subscribe [::subs/slider-items?])
        slider-position @(rf/subscribe [:get [:slider :position]])
        filter-results-overlay? @(rf/subscribe [:get [:ui :visible-dropdown]])]
    [:main
     (when (and slider-items? (= slider-position :top))
       [slider])
     [filters]

     [:div {:class (when filter-results-overlay?
                     "filter-results-overlay")
            :on-click #(when filter-results-overlay?
                         (rf/dispatch [::events/overlay-clicked]))}
      [grid-products-articles]
      [map-toggle-mobile]]

     (when (and slider-items? (= slider-position :bottom))
       [:div {:style {:padding-bottom "40px"}}
        [slider]])]))
